import React from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ReorderIcon from '@wix/da-ds/pkg/Icons/24x24/Reorder';
import EditIcon from '@wix/da-ds/pkg/Icons/16x16/Edit';
import TrashIcon from '@wix/da-ds/pkg/Icons/16x16/Trashcan';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import RadioButton from '@wix/da-ds/pkg/RadioButton';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import QuestionViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/OriginalsQuestion';
import { Question, AnswerType } from '../../../../types';
import RequiredMarker from '../../../RequiredMarker';

import s from './QuestionCard.scss';

interface Props {
  question: Question;
  moveHandleRef: React.RefObject<HTMLElement | null>;
  dropRef: React.RefObject<HTMLDivElement | null>;
  previewRef: React.RefObject<HTMLDivElement | null>;
  onEdit: (questionId: string) => void;
  onRemove: (questionId: string) => void;
  disableActions?: boolean;
}

const QuestionCard: React.FC<Props> = ({
  question,
  moveHandleRef,
  dropRef,
  previewRef,
  onEdit,
  onRemove,
  disableActions,
}) => {
  const { t } = useTranslation();
  return (
    <div className={s['root']} ref={previewRef}>
      <div className={s['drop-target']} ref={dropRef} />
      <div className={s['actions']}>
        <IconButton
          aria-labelledby="move-question"
          size="medium"
          variant="edit-purple"
          icon={ReorderIcon}
          innerRef={node => (moveHandleRef.current = node)}
          onClick={() => {}}
          disabled={disableActions}
        />
        <div className={s['side-actions']}>
          <TextButton
            size="small"
            variant="edit-purple"
            icon={EditIcon}
            onClick={() => onEdit(question.id)}
            disabled={disableActions}
          >
            {t('common.edit')}
          </TextButton>
          <TextButton
            size="small"
            variant="edit-purple"
            icon={TrashIcon}
            onClick={() => onRemove(question.id)}
            disabled={disableActions}
          >
            {t('common.delete')}
          </TextButton>
        </div>
      </div>
      <hr className={s['divider']} />
      {question.isRequired && <RequiredMarker />}
      <div className={s['question-body']}>
        <TextRenderer
          textContent={question.questionBody}
          tipTapViewer={QuestionViewer}
        />
      </div>
      <div className={s['answer-options']}>
        {question.answerChoices?.map((choice, index) => (
          <div className={s['answer-option']} key={index}>
            <div className={s['answer-option-input']}>
              {question.answerType === AnswerType.SINGLE_CHOICE && (
                <Checkbox
                  size="small"
                  originalGreen
                  isChecked={false}
                  disabled
                />
              )}
              {question.answerType === AnswerType.MULTIPLE_CHOICE && (
                <RadioButton
                  name="answer-option"
                  value={choice.answerText}
                  checked={false}
                  disabled
                />
              )}
            </div>
            <div className={s['answer-option-text']}>{choice.answerText}</div>
            <div className={s['answer-option-price']}>
              {formatDollars(choice.price)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;
